import { cn } from '@nextui-org/react'
import { IoMdDownload } from '@react-icons/all-files/io/IoMdDownload'
import { PiMicrosoftExcelLogoLight } from '@react-icons/all-files/pi/PiMicrosoftExcelLogoLight'
import sha256 from 'crypto-js/sha256'
import { TaskStatusDetail, TaskStatusDetailType, TaskStatuses } from '~/constants/task-status'
import { useTaskObj } from '~/hooks'
import { ModalId, useModalController } from '~/hooks/useModal'
import {
  AgentTaskStatus,
  DocumentInlineComment,
  TaskStatusAndRequestResponseTuple
} from '~/protos/agents'
import { csvToMarkdown } from '~/utils/shared'
import { cm } from '~/utils/utils'
import { CodePlanViewer } from '../code-plan/Planner'
import CodeEditor from '../CodeEditor'
import { CurieMarkdown } from '../markdown'
import { img as CurieMarkdownImg } from '../markdown/img'
import useAdminConversation from '~/hooks/super-admin/useAdminConversation'

export interface TaskStatusDetailModalProps extends TaskStatusDetail {
  title: string
  index: number
  taskStatus: AgentTaskStatus
  uuid?: string
}

export const getCodePlanFromTupleRequest = (tuple: TaskStatusAndRequestResponseTuple) => {
  return tuple.request?.codePlan
}

export const getCodePlanFromTupleResponse = (tuple: TaskStatusAndRequestResponseTuple) => {
  return tuple.response?.[0]?.responseContentStream?.codePlan
}

export const getDiffFromTupleRequest = (tuple: TaskStatusAndRequestResponseTuple) => {
  return tuple.request?.diff || []
}

export const getDiffFromTupleResponse = (tuple: TaskStatusAndRequestResponseTuple) => {
  return tuple.response?.[0]?.responseContentStream?.diff || []
}

export const getDocumentFromTupleResponse = (tuple: TaskStatusAndRequestResponseTuple) => {
  const document = tuple.response?.[0]?.responseContentStream?.document

  if (document != null) {
    for (const [key, value] of Object.entries(document!)) {
      if (value == null || value == null || value === '') {
        continue
      }

      let md = ''
      if (key === 'mappingSheetCsv') {
        md = csvToMarkdown(value)
      } else {
        md = value
      }
      return md
    }
  }

  return tuple.response?.[0]?.responseContentStream?.munitScenarios?.mdText
}

export const getCommentFromTupleRequest = (tuple: TaskStatusAndRequestResponseTuple) => {
  return tuple.request?.documentInlineComments?.[0] as DocumentInlineComment
}

export const getDocumentWithCommentFromTupleRequest = (
  tuples: TaskStatusAndRequestResponseTuple[],
  index: number
) => {
  const generatedTuple = tuples.findLast((tuple, tupleIndex) => {
    if (tupleIndex > index) {
      return false
    }
    return tuple.taskStatus === AgentTaskStatus.DOCUMENT_GENERATED
  })
  const comment = getCommentFromTupleRequest(tuples[index])
  return {
    markdown: getDocumentFromTupleResponse(generatedTuple!)!,
    comment: comment
  } as const
}

export const getNewCodePlanFromTuples = (
  tuples: TaskStatusAndRequestResponseTuple[],
  index: number
) => {
  const tuple = tuples[index]
  const completedTuple = tuples.findLast((tuple, tupleIndex) => {
    if (tupleIndex > index) {
      return false
    }
    return tuple.taskStatus === AgentTaskStatus.PLANNING_COMPLETED
  })
  if (completedTuple) {
    const originalCodePlan = getCodePlanFromTupleResponse(completedTuple)
    const editedCodePlan = getCodePlanFromTupleRequest(tuple)
    if (originalCodePlan && editedCodePlan) {
      const newCodePlan = JSON.parse(JSON.stringify(originalCodePlan))
      newCodePlan.fileMultiSelect!.selectedFileNames =
        editedCodePlan.fileMultiSelect!.selectedFileNames
      return newCodePlan
    }
  }
}

function highlightLines(markdown: string, startLine: number, endLine: number): string {
  const lines = markdown.split('\n')

  if (startLine > 0 && startLine <= lines.length) {
    const highlightCount = endLine - startLine + 1
    lines[startLine - 1] = `# [!code warning:${highlightCount}]\n${lines[startLine - 1]}`
  }

  return lines.join('\n')
}

export function TaskStatusDetailModal({ index, taskStatus, uuid }: TaskStatusDetailModalProps) {
  let taskObj = useTaskObj(uuid)
  const { closeModal } = useModalController<TaskStatusDetailModalProps>(ModalId.TaskStatusDetails)
  
  if (!taskObj && !!uuid) {
    const conversation = useAdminConversation({ uuid })
    taskObj = conversation?.task
  }

  const tuples = taskObj?.details.orderedStateResponses ?? []
  const tuple = tuples[index]
  const isDiffTaskStatus = TaskStatuses[taskStatus].detailType === TaskStatusDetailType.DIFF

  let text: string | undefined = undefined
  let className = isDiffTaskStatus
    ? 'min-w-[1200px] min-h-[700px] max-h-[900px]'
    : 'w-auto min-h-[400px] max-h-[600px]'
  let description: string | undefined = undefined

  const renderUploadedImages = (task: TaskObject | null) => {
    const images = task?.details.originalRequest?.command?.images || []
    return images.length > 0 ? (
      <div className='flex flex-col'>
        <div className='label font-semibold text-xs text-default-700'>Images Uploaded:</div>
        <div className='flex flex-wrap gap-2'>
          {images.map((image, index) => {
            const imageUrl = `data:image/${image.imageExtension};base64,${image.imageBase64Content}`
            return (
              <div key={index}>
                <CurieMarkdownImg
                  src={imageUrl}
                  alt='uploaded image'
                  className='inline-flex w-[100px] h-[100px] rounded'
                />
              </div>
            )
          })}
        </div>
      </div>
    ) : null
  }

  const renderUploadedSpreadsheets = (task: TaskObject | null) => {
    const spreadsheets = task?.details.originalRequest?.mappingSheet || []

    const handleDownload = (spreadsheet: { id: string; base64EncodedXlsx: string }) => {
      const blob = new Blob([Buffer.from(spreadsheet.base64EncodedXlsx, 'base64')], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = spreadsheet.id
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    }

    return spreadsheets.length > 0 ? (
      <div className='flex flex-col gap-2'>
        <div className='label font-semibold text-xs text-default-700'>Spreadsheet Uploaded:</div>
        <div className='flex flex-wrap gap-2'>
          {spreadsheets.map((spreadsheet, index) => {
            const file = new File([spreadsheet.base64EncodedXlsx], spreadsheet.id)

            return (
              <div key={index} className='w-full flex items-center justify-between mb-4'>
                <div
                  className={cn(
                    'border border-[#d4dce2] rounded-lg flex items-center min-w-[250px] flex-1',
                    'overflow-hidden text-ellipsis'
                  )}
                >
                  <div className='file-type h-full border-r-1 border-[#d4dce2] py-2 px-5'>
                    <PiMicrosoftExcelLogoLight size={32} color='#6f7987' />
                  </div>
                  <div
                    className={cn(
                      'ml-2 whitespace-nowrap text-sm text-default-700 max-w-[70%]',
                      'overflow-hidden text-ellipsis'
                    )}
                  >
                    {file.name}
                  </div>
                </div>

                <div
                  className={cn(
                    'button-submit bg-white text-default-600',
                    'px-[10px] rounded-lg cursor-pointer',
                    'border border-[#d4dce2] select-none h-8 hover:bg-light3',
                    'disabled:cursor-not-allowed',
                    'w-12 h-12 ml-2 flex items-center justify-center'
                  )}
                  onClick={() => handleDownload(spreadsheet)}
                >
                  <IoMdDownload size={16} className='w-[16px] h-[16px]' />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    ) : null
  }

  const renderDetail = () => {
    if (taskStatus === AgentTaskStatus.SUBMITTED) {
      text = taskObj?.details.originalRequest?.command?.commandString
      text = `\`\`\`\n${text}\n\`\`\``
      description = 'Task description:'
      return (
        <>
          {description ? (
            <div className='label font-semibold text-xs text-default-700 mb-[10px]'>
              {description}
            </div>
          ) : null}
          <CurieMarkdown className='flex-1 pl-0' text={text!} />
          {renderUploadedImages(taskObj)}
          {renderUploadedSpreadsheets(taskObj)}
        </>
      )
    } else if (TaskStatuses[taskStatus].detailType === TaskStatusDetailType.PLAN) {
      if (AgentTaskStatus.PLANNING_COMPLETED === taskStatus) {
        return <CodePlanViewer codePlan={getCodePlanFromTupleResponse(tuple)} />
      } else {
        const editedCodePlan = getNewCodePlanFromTuples(tuples, index)
        return editedCodePlan ? (
          <CodePlanViewer codePlan={editedCodePlan} />
        ) : (
          <CodePlanViewer codePlan={getCodePlanFromTupleRequest(tuple)} />
        )
      }
    } else if (TaskStatuses[taskStatus].detailType === TaskStatusDetailType.DIFF) {
      if (AgentTaskStatus.DIFF_GENERATED === taskStatus) {
        const initialDiffs = getDiffFromTupleResponse(tuple)
        const diffSha256 = sha256(JSON.stringify(initialDiffs)).toString()
        return (
          <div className='flex w-[1500px] h-[75vh] max-w-[85vw] rounded'>
            <CodeEditor key={diffSha256} initialDiffs={initialDiffs} readOnly />
          </div>
        )
      } else {
        const generatedTuple = tuples.findLast((response, tupleIndex) => {
          if (tupleIndex > index) {
            return false
          }
          return response.taskStatus === AgentTaskStatus.DIFF_GENERATED
        })
        if (generatedTuple) {
          const initialDiffs = getDiffFromTupleResponse(generatedTuple)
          const diffSha256 = sha256(JSON.stringify(initialDiffs)).toString()
          return (
            <div className='flex w-[1500px] h-[75vh] max-w-[85vw] rounded'>
              <CodeEditor key={diffSha256} initialDiffs={initialDiffs} readOnly />
            </div>
          )
        }
      }
    } else if (TaskStatuses[taskStatus].detailType === TaskStatusDetailType.DOC) {
      if (AgentTaskStatus.DOCUMENT_UPDATED === taskStatus) {
        const { markdown, comment } = getDocumentWithCommentFromTupleRequest(tuples, index)
        const highlighted = highlightLines(markdown, comment.startLineNumber, comment.endLineNumber)
        return (
          <>
            <div className='label font-semibold text-xs text-default-700 my-[15px] mt-[5px]'>
              User comment:
            </div>
            <div className='text-default-700 border border-outline rounded p-2 whitespace-break-spaces'>
              {comment.comment}
            </div>
            <CurieMarkdown text={highlighted} className='flex-1 px-2' />
          </>
        )
      } else {
        const markdown = getDocumentFromTupleResponse(tuple) as string
        return <CurieMarkdown className='flex-1 px-2' text={markdown} />
      }
    }
  }

  if (isDiffTaskStatus) {
    return renderDetail()
  }

  return (
    <div className={cm('flex w-full flex-col h-full p-4', className)}>
      <div className='h-[calc(100%-50px)] min-h-[300px] overflow-y-scroll px-[20px]'>
        {renderDetail()}
      </div>
      {/* <div className='button-groups flex items-center justify-end mt-[15px]'>
        <SecondaryButton onClick={closeModal} className='mx-2'>
          Close
        </SecondaryButton>
      </div> */}
    </div>
  )
}
